import { useTranslation } from "react-i18next"

import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useIsMobile } from "@/hooks/use-mobile"
import type {
  CandidateRow as CandidateRowType,
  CandidateSort,
} from "@/types/candidates"

import {
  CandidateRow,
  CandidateRowCard,
  type CandidateSelectionApi,
} from "./candidate-row"

/**
 * OKS-17 — Candidate table (≥640px) / card list (<640px).
 *
 * Same data, two layouts. Both renderings use the locked
 * `candidate-list-row` testid so QA Playwright can target a single
 * selector across viewports. Renders nothing when `rows.length === 0`
 * — caller is responsible for choosing between this component and the
 * empty-state / no-results panels.
 *
 * **OKS-19 additive — bulk selection.** Optional `selection` prop wires
 * the per-row `Checkbox` column + the header `Select all on this page`
 * tri-state checkbox. When omitted the table renders identically to
 * its OKS-17 shape.
 *
 * Locked testids (additive):
 *   - `candidate-list-bulk-select-all` — header tri-state checkbox
 *   - `candidate-list-row-checkbox-{id}` — per-row checkbox
 *
 * Tri-state semantics (UX Spec §Micro-Interactions): empty → false;
 * 1 ≤ n < count → "mixed" (`aria-checked="mixed"`); n == count → true.
 * Click toggles between two stable states only — false ↔ true; never
 * lands on "mixed" via a user click (Radix default).
 */
interface CandidateTableProps {
  rows: CandidateRowType[]
  sort: CandidateSort
  /**
   * OKS-19 — bulk-selection plumbing. When present, the table
   * renders a leading checkbox column + the header `Select all on
   * this page` tri-state checkbox. When absent (e.g. surfaces that
   * reuse the table without bulk semantics), the OKS-17 shape is
   * preserved verbatim. See `useBulkSelection` for the selection
   * state container.
   */
  selection?: CandidateSelectionApi & {
    selectAll: (visibleIds: readonly number[]) => void
    selectAllState: (visibleIds: readonly number[]) => boolean | "mixed"
  }
  /**
   * OKS-127 — recency-highlight marker, drilled from the
   * `just_added_id` Inertia prop. The table derives
   * `isJustAdded = row.id === justAddedId` per iteration and forwards
   * to `<CandidateRow>` / `<CandidateRowCard>`. `null` / `undefined`
   * ⇒ no highlight on any row. Filter / pagination miss (no row
   * matches the id in the current view) is a silent no-op per
   * UX Spec §1.Edge cases.
   */
  justAddedId?: number | null
  /**
   * OKS-465 — Removed-view mode. Adds a trailing Restore action column
   * (desktop) / card footer action (mobile) per row, driven by
   * `row.restore_url`. Bulk-selection affordances do not apply in the
   * Removed view, so the caller omits `selection` there.
   */
  removedView?: boolean
}

export function CandidateTable({
  rows,
  sort,
  selection,
  justAddedId,
  removedView = false,
}: CandidateTableProps) {
  const isMobile = useIsMobile()
  const { t } = useTranslation()

  const visibleIds = rows.map((row) => row.id)

  if (isMobile) {
    return (
      <div
        data-testid="candidate-list-table"
        className="overflow-hidden rounded-md border"
        role="list"
        aria-label={t("candidates.list.column.creator")}
      >
        {/*
          On mobile, the `Select all on this page` checkbox sits in a
          dedicated strip above the card list (UX Spec §Responsive —
          row checkbox column 44px wide). Mounted only when selection
          plumbing is provided.
        */}
        {selection && (
          <SelectAllStrip
            visibleIds={visibleIds}
            state={selection.selectAllState(visibleIds)}
            onSelectAll={() => selection.selectAll(visibleIds)}
          />
        )}
        {rows.map((row) => (
          <CandidateRowCard
            key={row.id}
            candidate={row}
            selection={selection}
            isJustAdded={justAddedId != null && row.id === justAddedId}
            removedView={removedView}
          />
        ))}
      </div>
    )
  }

  const sortIs = (
    field: CandidateSort["field"],
  ): "ascending" | "descending" | "none" => {
    if (sort.field !== field) return "none"
    return sort.dir === "asc" ? "ascending" : "descending"
  }

  return (
    <div
      data-testid="candidate-list-table"
      className="overflow-x-auto rounded-md border"
    >
      <Table>
        <TableHeader>
          <TableRow>
            {selection && (
              <TableHead scope="col" className="w-11 pr-1 pl-3">
                <SelectAllCheckbox
                  visibleIds={visibleIds}
                  state={selection.selectAllState(visibleIds)}
                  onSelectAll={() => selection.selectAll(visibleIds)}
                />
              </TableHead>
            )}
            <TableHead scope="col">
              {t("candidates.list.column.creator")}
            </TableHead>
            <TableHead
              scope="col"
              className="text-right"
              aria-sort={sortIs("approx_followers")}
            >
              {t("candidates.list.column.followers")}
            </TableHead>
            <TableHead scope="col">
              {t("candidates.list.column.status")}
            </TableHead>
            <TableHead scope="col" className="hidden md:table-cell">
              {t("candidates.list.column.location")}
            </TableHead>
            <TableHead scope="col" className="hidden lg:table-cell">
              {t("candidates.list.column.tags")}
            </TableHead>
            <TableHead scope="col" aria-sort={sortIs("created_at")}>
              {t("candidates.list.column.days_added")}
            </TableHead>
            {removedView && (
              <TableHead scope="col" className="text-right">
                <span className="sr-only">
                  {t("candidates.list.column.actions")}
                </span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <CandidateRow
              key={row.id}
              candidate={row}
              selection={selection}
              isJustAdded={justAddedId != null && row.id === justAddedId}
              removedView={removedView}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

interface SelectAllProps {
  visibleIds: readonly number[]
  state: boolean | "mixed"
  onSelectAll: () => void
}

/**
 * Header tri-state checkbox — desktop variant.
 *
 * Base UI `<Checkbox>` takes a boolean `checked` plus a separate
 * `indeterminate` boolean, and renders `aria-checked="mixed"` for the
 * indeterminate case. Clicking toggles between `false` and `true` only
 * — never lands on "mixed" via user gesture; that state is computed
 * from the underlying selection set against `visibleIds`.
 *
 * Empty visible-rows ⇒ disabled (no rows to select). Defensive: the
 * caller (CandidateTable) only renders when `rows.length > 0`, but
 * the disabled fallback hardens the contract for edge cases.
 */
function SelectAllCheckbox({ visibleIds, state, onSelectAll }: SelectAllProps) {
  const { t } = useTranslation()
  const indeterminate = state === "mixed"
  return (
    <Checkbox
      data-testid="candidate-list-bulk-select-all"
      aria-label={t("candidates.list.bulk.select_all_aria")}
      checked={indeterminate ? false : state}
      indeterminate={indeterminate}
      disabled={visibleIds.length === 0}
      onCheckedChange={() => onSelectAll()}
    />
  )
}

/**
 * Mobile (<640px) — dedicated `Select all on this page` strip above
 * the card list. Touch-target 44px (UX Spec §Responsive). Identical
 * tri-state semantics to the desktop header.
 */
function SelectAllStrip(props: SelectAllProps) {
  const { t } = useTranslation()
  return (
    <div className="bg-muted/30 flex min-h-11 items-center gap-3 border-b px-4">
      <SelectAllCheckbox {...props} />
      <span className="text-muted-foreground text-xs">
        {t("candidates.list.bulk.select_all_label")}
      </span>
    </div>
  )
}
